/**
 * Test-admin session marker
 * ------------------------------------------------------------------
 * Sessions minted via /admin/test-login are sandbox seats that share a
 * single bootstrap key. We tag them client-side so the AdminAuth shell can
 * apply stricter session-timeout rules without affecting real admins who
 * authenticate with email + password.
 *
 * The marker lives in `sessionStorage` (not `localStorage`) so closing the
 * tab also drops the flag. We also write the original mint timestamp so the
 * absolute-cap timer survives a page reload mid-session.
 */
const STORAGE_KEY = 'yo:admin:test-session';
export const TEST_ADMIN_EMAIL = 'test-admin@yohq.local';

export interface TestAdminSessionMarker {
  v: 1;
  mintedAt: number;
  email: string;
}

export const markTestAdminSession = (email = TEST_ADMIN_EMAIL): void => {
  try {
    const payload: TestAdminSessionMarker = {
      v: 1,
      mintedAt: Date.now(),
      email,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage may be unavailable in some embedded contexts; ignore. */
  }
};

export const readTestAdminSession = (): TestAdminSessionMarker | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TestAdminSessionMarker>;
    if (parsed?.v !== 1 || typeof parsed.mintedAt !== 'number' || typeof parsed.email !== 'string') {
      return null;
    }
    return parsed as TestAdminSessionMarker;
  } catch {
    return null;
  }
};

export const clearTestAdminSession = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Belt-and-braces check: a session is considered "test admin" if either the
 * marker is present OR the active user's email matches the canonical test
 * email. The email match guards against the marker being lost (e.g. a tab
 * crash + restore) while the user is still on the sandbox seat.
 */
export const isTestAdminEmail = (email: string | null | undefined): boolean =>
  Boolean(email && email.toLowerCase() === TEST_ADMIN_EMAIL);